"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useAggregateCommand } from "@/hooks/commands";
import { Sparkles, Clock, ArrowRight, User, MessageCircle, FileText, XCircle, ChevronDown, ChevronUp, Shield, Building2, MapPin, Loader2, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { useTenant } from "@/lib/tenant";
import type { DealWithRelations, DealDocumentWithHref } from "@/lib/deals/types";
import { getSellerStepForStatus, getBuyerStepForStatus, getVisibleDocuments, isIdentityRevealed, CANCELLABLE_STATUSES, DEAL_STATUS_DESCRIPTIONS } from "@/lib/deals/types";
import type { DealStatus } from "@/lib/deals/types";
import { DEAL_STATUS_STYLE, DEAL_STATUS_LABELS, getDealActionHint, categorizeDeal, type DealPerspective } from "@/lib/deals/status-config";
import type { AnonymizedDealRelations } from "@/lib/platform";
import { ANONYMOUS_SELLER } from "@/lib/platform";
import { DealProgress } from "@/components/deals/DealProgress";
import { DealStepModal } from "@/components/deals/DealStepModal";
import { DealDocumentsSection } from "@/components/deals/DealDocumentsSection";
import { DealChatModal } from "@/components/deals/DealChatModal";
import { DealPositionsList } from "@/components/deals/DealPositionsList";
import { dealTotal, tradedPositions } from "@/lib/deals/positions";
import { positionPrice, positionQuantity, positionSubject } from "@/lib/deals/position-labels";

// --- Steps where the user may need to take action ---

const ACTIONABLE_STEPS = new Set(["identity_verification", "eco_account_verification", "contract_preparation", "contract", "payment_pending"]);

// --- Perspective-specific helpers ---

function getIdentity(deal: DealWithRelations, perspective: DealPerspective, dealNumber?: number): string {
  const anonymousName = perspective === "buyer" ? ANONYMOUS_SELLER : dealNumber ? `Interessent #${dealNumber}` : "Interessent";
  if (!isIdentityRevealed(deal.status)) return anonymousName;
  const counterparty = perspective === "seller" ? deal.buyer : deal.seller;
  if (!counterparty) return anonymousName;
  if (counterparty.company_name) return counterparty.company_name;
  const fullName = [counterparty.first_name, counterparty.last_name].filter(Boolean).join(" ");
  return fullName || anonymousName;
}
function getLocation(deal: DealWithRelations, perspective: DealPerspective): string {
  if (perspective === "buyer") {
    const {
      landkreis,
      bundesland
    } = deal.offering ?? {};
    if (landkreis && bundesland) return `${landkreis}, ${bundesland}`;
    return bundesland || "Deutschland";
  }
  const {
    landkreis,
    bundesland
  } = deal.request ?? {};
  if (landkreis && bundesland) return `${landkreis}, ${bundesland}`;
  return bundesland || "Interessent";
}

// --- Anonymize deal for DealStepModal ---

type AnonymizedDeal = Omit<DealWithRelations, "seller" | "buyer"> & AnonymizedDealRelations;
function anonymizeDeal(deal: DealWithRelations): AnonymizedDeal {
  const revealed = isIdentityRevealed(deal.status);
  return {
    ...deal,
    seller: revealed ? deal.seller : {
      id: deal.seller_id ?? "",
      first_name: null,
      last_name: null,
      company_name: "Anbieter"
    },
    buyer: revealed ? deal.buyer : {
      id: deal.buyer_id ?? "",
      first_name: null,
      last_name: null,
      company_name: "Interessent"
    }
  };
}

// --- Waiting context icon helper ---

function WaitingContextHint(t0) {
  const $ = _c(12);
  const {
    hint
  } = t0;
  const ctx = hint.context;
  if (ctx === "admin") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Shield className="w-3.5 h-3.5 flex-shrink-0" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== hint.text) {
      t2 = <p className="text-xs text-blue-600 flex items-center gap-1.5 bg-blue-50 px-3 py-2 rounded-lg">{t1}{hint.text}</p>;
      $[1] = hint.text;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (ctx === "external") {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Building2 className="w-3.5 h-3.5 flex-shrink-0" />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== hint.text) {
      t2 = <p className="text-xs text-sky-600 flex items-center gap-1.5 bg-sky-50 px-3 py-2 rounded-lg">{t1}{hint.text}</p>;
      $[4] = hint.text;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (ctx === "other_party") {
    let t1;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Clock className="w-3.5 h-3.5 flex-shrink-0" />;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] !== hint.text) {
      t2 = <p className="text-xs text-zinc-500 flex items-center gap-1.5">{t1}{hint.text}</p>;
      $[7] = hint.text;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Clock className="w-3.5 h-3.5" />;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== hint.text) {
    t2 = <p className="mt-3 text-xs text-zinc-500 flex items-center gap-1">{t1}{hint.text}</p>;
    $[10] = hint.text;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

// --- Component ---

interface UnifiedDealCardProps {
  deal: DealWithRelations;
  perspective: DealPerspective;
  userId: string;
  dealNumber?: number;
  totalDeals?: number;
  onRefresh?: () => void;
}
export function UnifiedDealCard({
  deal,
  perspective,
  userId,
  dealNumber,
  totalDeals,
  onRefresh
}: UnifiedDealCardProps) {
  const tenant = useTenant();
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [checkingStep, setCheckingStep] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [adminFeedback, setAdminFeedback] = useState<string | null>(null);
  const category = categorizeDeal(deal.status);
  const style = DEAL_STATUS_STYLE[deal.status] || DEAL_STATUS_STYLE.inquiry_pending;
  const StatusIcon = style.icon;
  const statusLabel = DEAL_STATUS_LABELS[perspective][deal.status];
  const currentStep = perspective === "seller" ? getSellerStepForStatus(deal.status) : getBuyerStepForStatus(deal.status);
  const isStaticallyActionable = deal.status !== "cancelled" && deal.status !== "completed" && ACTIONABLE_STEPS.has(currentStep) && !(currentStep === "contract_preparation" && deal.status !== "contract_review");
  const actionHint = getDealActionHint(perspective, deal.status, stepCompleted);
  const canCancel = deal.status !== "cancelled" && deal.status !== "completed" && CANCELLABLE_STATUSES.includes(deal.status as DealStatus);
  const {
    send: sendCancel
  } = useAggregateCommand<{
    reason: null;
  }, unknown>({
    method: "POST"
  });
  const handleCancel = useCallback(async () => {
    setIsCancelling(true);
    try {
      // Decided on the deal as the card shows it: a deal that moved on since is read again.
      const outcome = await sendCancel({
        url: `/api/deals/${deal.id}/cancel`,
        input: {
          reason: null
        },
        revision: deal.revision
      });
      if (outcome.kind === "committed" || outcome.kind === "conflict") {
        setShowCancelConfirm(false);
        onRefresh?.();
      }
    } finally {
      setIsCancelling(false);
    }
  }, [deal.id, deal.revision, onRefresh, sendCancel]);
  const isNewMatch = deal.status === "reserved";
  // What the deal trades: one position's figures, or, for several, each position and their total.
  const traded = tradedPositions(deal.positions ?? []);
  const only = traded.length === 1 ? traded[0]! : null;
  const quantity = only?.quantity != null ? positionQuantity(only) : null;
  const price = only ? positionPrice(only) : null;
  const totalValue = dealTotal(deal.positions ?? []);
  const {
    documents,
    anonymizedDeal
  } = useMemo(() => {
    const docs: DealDocumentWithHref[] = getVisibleDocuments(deal, perspective);
    const anon = anonymizeDeal(deal);
    return {
      documents: docs,
      anonymizedDeal: anon
    };
  }, [deal, perspective]);

  // --- Detect return from DocuSign signing ceremony ---
  // When the user signs in a new tab, the return URL includes ?event=signing_complete.
  // Sync the signature status so the DB is up-to-date before the step check runs.
  useEffect(() => {
    if (currentStep !== "contract") return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("event") !== "signing_complete") return;
    const syncSigning = async () => {
      try {
        await fetch(`/api/deals/${deal.id}/signature/sync`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            completed: true
          })
        });
      } catch {
        // Ignore — step completion check will still run
      }
      // Clean up the URL query param (only once)
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      url.searchParams.delete("envelope_id");
      url.searchParams.delete("stub");
      window.history.replaceState({}, "", url.toString());
      // Trigger step re-check
      setRefreshCounter(c => c + 1);
    };
    syncSigning();
  }, [deal.id, currentStep]);

  // --- Step completion check (fetches per-step data) ---
  useEffect(() => {
    if (!isStaticallyActionable || category === "inactive") {
      setStepCompleted(false);
      setCheckingStep(false);
      return;
    }

    // Reset before each check — prevents stale state when step/status changes
    setStepCompleted(false);
    setCheckingStep(true);
    const controller = new AbortController();
    const signal = controller.signal;
    const check = async () => {
      let completed = false;
      try {
        const prefix = perspective === "seller" ? "seller" : "buyer";
        switch (currentStep) {
          case "identity_verification":
            {
              const crRes_1 = await fetch(`/api/deals/${deal.id}/contract-review`, {
                signal
              });
              if (crRes_1.ok) {
                const cr_1 = (await crRes_1.json()).data;
                // Both buyer and seller only need address for now
                // (identity upload is disabled for sellers)
                completed = !!cr_1?.[`${prefix}_billing_name`];
              }
              break;
            }
          case "eco_account_verification":
            {
              if (perspective === "buyer") {
                // Buyer: check if billing address was submitted
                const crRes_0 = await fetch(`/api/deals/${deal.id}/contract-review`, {
                  signal
                });
                if (crRes_0.ok) {
                  const cr_0 = (await crRes_0.json()).data;
                  completed = !!cr_0?.buyer_billing_name;
                }
              } else {
                // Seller: check ökokonto document
                const verRes = await fetch(`/api/deals/${deal.id}/verification`, {
                  signal
                });
                if (verRes.ok) {
                  const ver = (await verRes.json()).data;
                  const okokontoDone = !!ver?.okokonto_document_url || !!deal.offering?.okokonto_document_url;
                  completed = okokontoDone;
                  // Capture verification status for seller banners
                  setVerificationStatus(ver?.verification_status ?? null);
                  setAdminFeedback(ver?.admin_feedback ?? null);
                }
              }
              break;
            }
          case "contract_preparation":
            {
              // In contract_review status, check if user already accepted the draft
              if (deal.status === "contract_review") {
                const crRes = await fetch(`/api/deals/${deal.id}/contract-review`, {
                  signal
                });
                if (crRes.ok) {
                  const cr = (await crRes.json()).data;
                  completed = cr?.[`${prefix}_contract_accepted`] === true;
                }
              }
              break;
            }
          case "contract":
            {
              // If user has signed, the step is done from their perspective
              // (terms acceptance is a prerequisite for signing, not a separate action)
              const sigRes = await fetch(`/api/deals/${deal.id}/signature`, {
                signal
              });
              if (sigRes.ok) {
                const sig = (await sigRes.json()).data;
                completed = sig?.[`${prefix}_signature_status`] === "signed";
              }
              break;
            }
          case "payment_pending":
            {
              const res = await fetch(`/api/deals/${deal.id}/payment`, {
                signal
              });
              if (res.ok) {
                const pay = (await res.json()).data;
                completed = perspective === "seller" ? !!pay?.seller_payment_sent : !!(pay?.buyer_payment_received && pay?.buyer_commission_received);
              }
              break;
            }
        }
      } catch {
        // AbortError or network — ignore
        return;
      }
      // Batch state updates together so React renders once
      setStepCompleted(completed);
      setCheckingStep(false);
    };
    check();
    return () => controller.abort();
  }, [deal.id, deal.status, deal.offering?.okokonto_document_url, currentStep, perspective, isStaticallyActionable, category, refreshCounter]);

  // Left border color by category
  const borderClass = isNewMatch ? "border-l-4 border-l-amber-400" : category === "inProgress" ? "border-l-4" : category === "completed" ? "border-l-4 border-l-emerald-400" : "border-l-4 border-l-zinc-300";
  const borderStyle = !isNewMatch && category === "inProgress" ? {
    borderLeftColor: tenant.theme.primary
  } : undefined;
  return <>
      <DealStepModal isOpen={isStepModalOpen} onClose={() => setIsStepModalOpen(false)} deal={anonymizedDeal} userRole={perspective} userId={userId} onRefresh={() => {
      setRefreshCounter(c_0 => c_0 + 1);
      onRefresh?.();
    }} />

      <DealChatModal isOpen={isChatModalOpen} onClose={() => setIsChatModalOpen(false)} deal={deal} perspective={perspective} userId={userId} dealNumber={dealNumber} />

      <div className={`bg-white rounded-xl border border-zinc-200 ${borderClass}`} style={borderStyle}>
        <div className="p-4">
          {/* Status badge row */}
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
              {category === "inactive" ? <>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {statusLabel}
                </> : <>
                  Vorgang {dealNumber} von {totalDeals}
                </>}
            </span>

            {/* Compact progress dots (skip for inactive/terminal) */}
            {category !== "inactive" && <div className="flex-1 ml-4 w-full">
                <DealProgress currentStatus={deal.status} userRole={perspective} compact />
              </div>}
          </div>

          {/* 50/50 info columns */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Deal Details */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                Vorgangsdetails
              </p>
              <div className="space-y-1.5 text-xs">
                {traded.length > 0 && <div className="flex items-baseline justify-between">
                    <span className="text-zinc-400">Gegenstand</span>
                    <span className="font-medium text-zinc-800">
                      {traded.map(positionSubject).join(" + ")}
                    </span>
                  </div>}
                {traded.length > 1 && <DealPositionsList positions={deal.positions ?? []} />}
                {quantity && <div className="flex items-baseline justify-between">
                    <span className="text-zinc-400">Menge</span>
                    <span className="font-medium text-zinc-800">{quantity}</span>
                  </div>}
                {price && <div className="flex items-baseline justify-between">
                    <span className="text-zinc-400">Preis</span>
                    <span className="font-medium text-zinc-800">{price}</span>
                  </div>}
                {totalValue != null && traded.length === 1 && <div className="flex items-baseline justify-between pt-1 border-t border-zinc-100">
                    <span className="text-zinc-400">Gesamt</span>
                    <span className="font-semibold text-primary">
                      {totalValue.toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{" "}
                      EUR
                    </span>
                  </div>}
                {traded.length <= 1 && !quantity && !price && <p className="text-zinc-400 italic">Noch nicht festgelegt</p>}
              </div>
            </div>

            {/* Right: Who */}
            <div className="border-l border-zinc-100 pl-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                {perspective === "buyer" ? "Anbieter" : "Interessent"}
              </p>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isNewMatch ? "bg-amber-100" : ""}`} style={!isNewMatch ? {
                  backgroundColor: `${tenant.theme.primary}10`
                } : undefined}>
                    {isNewMatch ? <Sparkles className="w-3 h-3 text-amber-600" /> : <User className="w-3 h-3 text-zinc-500" />}
                  </div>
                  <span className={`font-medium ${isNewMatch ? "text-amber-900" : "text-zinc-800"}`}>
                    {getIdentity(deal, perspective, dealNumber)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-zinc-500">
                  <MapPin className="w-3 h-3 text-zinc-400 flex-shrink-0" />
                  {getLocation(deal, perspective)}
                </div>
              </div>
            </div>
          </div>

          {/* Verification status banner for seller */}
          {verificationStatus === "needs_info" && perspective === "seller" && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Weitere Informationen benötigt
                  </p>
                  {adminFeedback && <p className="text-xs text-blue-700 mt-1">{adminFeedback}</p>}
                  <button onClick={() => setIsStepModalOpen(true)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors">
                    Dokument hochladen
                  </button>
                </div>
              </div>
            </div>}

          {verificationStatus === "verified" && perspective === "seller" && <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Ökokonto verifiziert</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Ihr Ökokonto-Dokument wurde erfolgreich geprüft.
                  </p>
                </div>
              </div>
            </div>}

          {verificationStatus === "failed" && perspective === "seller" && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Ökokonto-Prüfung nicht bestanden
                  </p>
                  {adminFeedback && <p className="text-xs text-red-700 mt-1">{adminFeedback}</p>}
                </div>
              </div>
            </div>}

          {/* Action buttons */}
          <div className={`mt-4 pt-3 border-t ${isNewMatch ? "border-amber-200/50" : "border-zinc-100"} flex flex-wrap items-center gap-2`}>
            <div>
              {/* Urgent action: pulsing button with contextual hint text */}
              {actionHint?.urgent && !checkingStep && <button onClick={() => setIsStepModalOpen(true)} className="relative inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors">
                  <span className="absolute -top-1 -right-1 w-3 h-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
                  </span>
                  {actionHint.text}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>}

              {/* Non-urgent: contextual waiting hint */}
              {actionHint && !actionHint.urgent && <WaitingContextHint hint={actionHint} />}

              {/* Cancellation reason */}
              {deal.status === "cancelled" && deal.cancellation_reason && <div className="mt-3 p-2 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-700 flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {deal.cancellation_reason}
                  </p>
                </div>}

              {/* Completion notice */}
              {deal.status === "completed" && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      Vorgang erfolgreich abgeschlossen
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      {perspective === "seller" ? "Auszahlung wurde veranlasst." : "Ökopunkte wurden übertragen."}
                    </p>
                  </div>
                </div>}

              {/* Invalidation notice */}
              {deal.status === "invalidated" && <div className="mt-3 p-2 bg-zinc-100 rounded-lg">
                  <p className="text-xs text-zinc-600 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    {DEAL_STATUS_DESCRIPTIONS.invalidated}
                  </p>
                  {(deal as {
                invalidation_reason?: string;
              }).invalidation_reason && <p className="text-xs text-zinc-500 mt-1">
                      Grund: {(deal as {
                  invalidation_reason?: string;
                }).invalidation_reason}
                    </p>}
                </div>}
            </div>
            {/* Chat modal trigger */}
            <button onClick={() => setIsChatModalOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors">
              <MessageCircle className="w-3.5 h-3.5" />
              Nachricht
            </button>

            {/* Documents toggle (only if documents exist) */}
            {documents.length > 0 && <button onClick={() => setIsDocsOpen(!isDocsOpen)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors">
                <FileText className="w-3.5 h-3.5" />
                Dokumente ({documents.length})
                {isDocsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>}

            {/* Cancel deal button (right-aligned, early stages only) */}
            {canCancel && !showCancelConfirm && <button onClick={() => setShowCancelConfirm(true)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-400 hover:text-red-600 transition-colors">
                <XCircle className="w-3.5 h-3.5" />
                Stornieren
              </button>}
          </div>

          {/* Cancel confirmation */}
          {showCancelConfirm && <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Vorgang stornieren?</p>
                  <p className="text-sm text-red-600 mt-1">
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowCancelConfirm(false)} disabled={isCancelling} className="flex-1 py-2 border border-zinc-200 text-zinc-700 rounded-lg text-sm font-medium hover:bg-zinc-50 disabled:opacity-50">
                  Abbrechen
                </button>
                <button onClick={handleCancel} disabled={isCancelling} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isCancelling ? <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird storniert...
                    </> : "Ja, stornieren"}
                </button>
              </div>
            </div>}

          {/* Collapsible documents section */}
          {isDocsOpen && documents.length > 0 && <div className="mt-3">
              <DealDocumentsSection dealId={deal.id} documents={documents} userId={userId} onDocumentUploaded={onRefresh} compact />
            </div>}
        </div>
      </div>
    </>;
}